/**
 * NotificationBell — navbar bell icon with dropdown
 * Shows unread count, last 5 notifications, and links to full /notifications page.
 *
 * Polls /api/notifications/unread-count every 60s for low-bandwidth usage.
 * Full list fetch happens only when dropdown is opened.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BookOpen, Megaphone, GraduationCap, Briefcase, AlertCircle, X, Check, CheckCheck } from 'lucide-react';
import apiService from '../services/api';

const TYPE_META = {
  lab_update:   { icon: BookOpen,    color: 'text-blue-500',    label: 'Lab Update' },
  announcement: { icon: Megaphone,   color: 'text-amber-500',   label: 'Announcement' },
  batch_news:   { icon: GraduationCap, color: 'text-emerald-500', label: 'Batch News' },
  job_alert:    { icon: Briefcase,   color: 'text-purple-500',  label: 'Job Alert' },
  system:       { icon: AlertCircle, color: 'text-slate-500',   label: 'System' },
};

const SEVERITY_RING = {
  info: '',
  warning: 'ring-2 ring-amber-200',
  critical: 'ring-2 ring-rose-300',
};

function timeAgo(iso) {
  if (!iso) return '';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  const diff = Math.max(0, Date.now() - d.getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // ---- poll unread count ----
  const refreshCount = useCallback(async () => {
    try {
      const r = await apiService.getNotificationsUnreadCount();
      setUnreadCount(r?.unread_count ?? 0);
    } catch {
      // silently ignore — notifications are non-critical
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, 60_000);
    return () => clearInterval(id);
  }, [refreshCount]);

  // ---- close on outside click ----
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiService.getNotifications({ limit: 5 });
      setItems(r?.notifications || []);
      setUnreadCount(r?.unread_count ?? 0);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchItems();
  };

  const onItemClick = async (n) => {
    try {
      if (!n.is_read) await apiService.markNotificationRead(n.id);
    } catch { /* ignore */ }
    setOpen(false);
    if (n.link) {
      navigate(n.link);
    } else {
      navigate('/notifications');
    }
  };

  const onMarkAll = async () => {
    try {
      await apiService.markAllNotificationsRead();
      await fetchItems();
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  const onDismiss = async (e, id) => {
    e.stopPropagation();
    try {
      await apiService.dismissNotification(id);
      setItems((prev) => prev.filter((x) => x.id !== id));
      refreshCount();
    } catch { /* ignore */ }
  };

  return (
    <div
      className="fixed bottom-6 right-6 z-[9999]"
      ref={dropdownRef}
      data-testid="notification-bell"
    >
      <button
        type="button"
        onClick={toggle}
        className="relative inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-slate-800 to-slate-900 shadow-xl hover:shadow-2xl border border-white/20 hover:scale-105 transition-all focus:outline-none focus:ring-2 focus:ring-emerald-400"
        aria-label="Notifications"
        data-testid="notification-bell-button"
      >
        <Bell className="w-5 h-5 text-white" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[20px] h-[20px] px-1 inline-flex items-center justify-center rounded-full bg-rose-500 text-white text-[11px] font-bold leading-none ring-2 ring-white shadow-lg"
            data-testid="notification-bell-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute bottom-14 right-0 w-[360px] max-w-[92vw] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
            <div className="font-semibold text-slate-800">Notifications</div>
            {unreadCount > 0 && (
              <button
                onClick={onMarkAll}
                className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
                data-testid="notification-mark-all-read"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[420px] overflow-y-auto">
            {loading && (
              <div className="p-6 text-center text-sm text-slate-500">Loading…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="p-8 text-center">
                <Bell className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                <div className="text-sm font-medium text-slate-600">You're all caught up</div>
                <div className="text-xs text-slate-400 mt-1">No notifications right now.</div>
              </div>
            )}
            {!loading && items.map((n) => {
              const meta = TYPE_META[n.type] || TYPE_META.system;
              const Icon = meta.icon;
              const ring = SEVERITY_RING[n.severity] || '';
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => onItemClick(n)}
                  className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition ${
                    n.is_read ? 'bg-white' : 'bg-blue-50/40'
                  }`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className={`flex-shrink-0 w-8 h-8 rounded-lg bg-slate-100 ${ring} flex items-center justify-center mt-0.5`}>
                    <Icon className={`w-4 h-4 ${meta.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-semibold text-slate-800 line-clamp-2">{n.title}</div>
                      {!n.is_read && (
                        <span className="flex-shrink-0 w-2 h-2 rounded-full bg-blue-500 mt-1.5" />
                      )}
                    </div>
                    <div className="text-xs text-slate-600 line-clamp-2 mt-0.5">{n.message}</div>
                    <div className="flex items-center justify-between mt-1">
                      <div className="text-[11px] text-slate-400">{timeAgo(n.created_at)}</div>
                      {n.requires_action && (
                        <span className="text-[10px] uppercase tracking-wide text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded font-bold">
                          Action needed
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => onDismiss(e, n.id)}
                    className="flex-shrink-0 w-6 h-6 inline-flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                    aria-label="Dismiss"
                    data-testid={`notification-dismiss-${n.id}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <button
            onClick={() => { setOpen(false); navigate('/notifications'); }}
            className="w-full text-center py-2.5 text-sm font-medium text-emerald-600 hover:bg-emerald-50 transition border-t border-slate-100"
            data-testid="notification-view-all"
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
}
